/**
 * Utility function that translates a boolean value into a localized or dynamic string.
 *
 * @param value - The boolean value to determine the result.
 * @param t - A translation function that takes a string key and returns the corresponding translated or dynamic value.
 * @param yes - The key to use for the "true" case (default: "desc.yes").
 * @param no - The key to use for the "false" case (default: "desc.no").
 * @returns A string corresponding to the translated "yes" or "no" key, based on the boolean value.
 */
export function shortYesNo(value: boolean, t: (value: string) => string, yes = "desc.yes", no = "desc.no") {
  return value ? t(yes) : t(no);
}

/**
 * Utility function that determines if an object with a `length` property has a non-zero length,
 * and returns a localized or dynamic string based on the result.
 *
 * @template T - Type of the object being checked, which must have an optional `length` property.
 * @param value - The object with a `length` property to check.
 * @param t - A translation function that takes a string key and returns the corresponding translated or dynamic value.
 * @param yes - The key to use for the "true" case (default: "desc.yes").
 * @param no - The key to use for the "false" case (default: "desc.no").
 * @returns A string corresponding to the translated "yes" or "no" key, based on whether the `length` is greater than zero.
 */
export function hasNonZeroLengthYesNo<T extends { length?: number }>(
  value: T,
  t: (val: string) => string,
  yes = "desc.yes",
  no = "desc.no",
) {
  return shortYesNo((value?.length ?? 0) > 0, t, yes, no);
}
